import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PlusIcon } from "@heroicons/react/outline";
import { useAppDispatch } from "src/common/state/hooks";
import {
  refetchAgentsThunk,
  deleteAgentThunk,
  getRagAgents,
  getRagAgentsStatus,
} from "src/features/rag/state/ragSlice";
import AgentCard from "src/features/rag/components/AgentCard";
import EmptyState from "src/features/statsNew/components/EmptyState";
import { SearchInput } from "src/common/components/SearchInput";
import ModalConfirmation from "src/common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";

const DELETE_AGENT_LIST_MODAL_ID = "delete-rag-agent-list-confirmation";

const SkeletonCard = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm animate-pulse">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-full" />
      </div>
      <div className="w-10 h-10 bg-gray-200 rounded-lg" />
    </div>
    <div className="flex gap-4 mt-4 pt-3 border-t border-gray-100">
      <div className="h-3 bg-gray-200 rounded w-20" />
      <div className="h-3 bg-gray-200 rounded w-24" />
    </div>
  </div>
);

const AgentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const agents = useSelector(getRagAgents);
  const status = useSelector(getRagAgentsStatus);
  // Only show the skeleton when there is genuinely nothing to display yet; a
  // background refresh over already-loaded data must not flash the skeleton.
  const loading = (status === "idle" || status === "loading") && agents.length === 0;
  const [search, setSearch] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const handleDeleteRequest = (agentId: number) => {
    setPendingDeleteId(agentId);
    dispatch(openConfirmationModal(DELETE_AGENT_LIST_MODAL_ID));
  };

  const handleDeleteConfirm = async () => {
    if (pendingDeleteId === null) return;
    try {
      await dispatch(deleteAgentThunk(pendingDeleteId)).unwrap();
      dispatch(setAlert({ type: "success", msg: t("pages.agents.agentDeleted") }));
    } catch (err) {
      console.error("Failed to delete agent:", err);
    }
    setPendingDeleteId(null);
    dispatch(closeConfirmationModal(DELETE_AGENT_LIST_MODAL_ID));
  };

  // Management view: always pull the authoritative server state on mount.
  useEffect(() => {
    dispatch(refetchAgentsThunk());
  }, [dispatch]);

  const ownAgentsAll = agents.filter((a) => !a.is_shared);
  const totalDocuments = ownAgentsAll.reduce((sum, a) => sum + Number(a.document_count ?? 0), 0);
  const totalChunks = ownAgentsAll.reduce((sum, a) => sum + Number(a.total_chunks ?? 0), 0);

  const { ownAgents, sharedAgents } = useMemo(() => {
    let filtered = agents;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = agents.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.description && a.description.toLowerCase().includes(q)),
      );
    }
    return {
      ownAgents: filtered.filter((a) => !a.is_shared),
      sharedAgents: filtered.filter((a) => a.is_shared),
    };
  }, [agents, search]);

  const filteredAgents = [...ownAgents, ...sharedAgents];

  return (
    <div className="h-full flex flex-col">
      {/* Action bar */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        {/* KPI summary */}
        {!loading && agents.length > 0 && (
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("pages.agents.title")}</p>
                <p className="text-sm font-bold text-gray-900">{ownAgentsAll.length} / 10</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("pages.agents.assignedDocuments")}</p>
                <p className="text-sm font-bold text-gray-900">{totalDocuments}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Chunks</p>
                <p className="text-sm font-bold text-gray-900">{totalChunks}</p>
              </div>
            </div>
          </div>
        )}
        {(loading || agents.length === 0) && <div />}

        <div className="flex items-center gap-3">
          {!loading && agents.length > 0 && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("general.search") + "..."}
              className="min-w-[220px]"
            />
          )}
          <button
            onClick={() => navigate("/agents/new")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            {t("pages.agents.createAgent")}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : agents.length === 0 ? (
        <div className="flex-1 flex items-start justify-center pt-[10%]">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-12 py-16 max-w-lg w-full text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("pages.agents.noAgents")}
            </h3>
            <p className="text-sm text-gray-500 mb-6">{t("pages.agents.noAgentsDescription")}</p>
            <button
              onClick={() => navigate("/agents/new")}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              {t("pages.agents.createAgent")}
            </button>
          </div>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon="search"
            title={t("statsNew.emptyState.noSearchResults")}
            description={t("statsNew.emptyState.noSearchResultsDescription")}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Own agents */}
          {ownAgents.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {ownAgents.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onClick={() => navigate(`/agents/${agent.id}`)}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </div>
          )}

          {/* Shared agents section */}
          {sharedAgents.length > 0 && (
            <>
              {ownAgents.length > 0 && (
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
                  {t("pages.agents.ragAgentSelect.sharedAgents")}
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sharedAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <ModalConfirmation
        id={DELETE_AGENT_LIST_MODAL_ID}
        title={t("pages.agents.deleteAgent")}
        text={t("pages.agents.deleteAgentConfirm")}
        onClickSubmit={handleDeleteConfirm}
      />
    </div>
  );
};

export default AgentList;
